import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FolderKanban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api, unwrap } from "@/lib/api";
import { getLoginMode, rememberLoginMode, setToken } from "@/lib/auth";

type SessionUser = { id: number; name: string | null; role: "user" | "admin"; nameSetupRequired: boolean };

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return fallback;
}

export default function LoginPage() {
  const [mode, setMode] = useState(getLoginMode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const qc = useQueryClient();

  const finishLogin = (data: { token: string; user: SessionUser }) => {
    rememberLoginMode(mode);
    setToken(data.token);
    qc.setQueryData(["auth", "me"], data.user);
    qc.invalidateQueries({ queryKey: ["auth", "me"] });
    window.location.href = data.user.nameSetupRequired ? "/setup-name" : "/dashboard";
  };

  const loginMutation = useMutation({
    mutationFn: async (vars: { username: string; password: string }) =>
      unwrap(api.POST("/api/auth/login", { body: vars })),
    onSuccess: finishLogin,
    onError: (err: unknown) => toast.error(errorMessage(err, "Invalid username or password")),
  });

  const requestCodeMutation = useMutation({
    mutationFn: async () => unwrap(api.POST("/api/auth/email/request", { body: { email: email.trim() } })),
    onSuccess: () => {
      setCodeSent(true);
      toast.success("Verification code sent — check your inbox");
    },
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to send code")),
  });

  const verifyMutation = useMutation({
    mutationFn: async () =>
      unwrap(
        api.POST("/api/auth/email/verify", {
          body: { email: email.trim(), code: code.trim() },
        }),
      ),
    onSuccess: finishLogin,
    onError: (err: unknown) => toast.error(errorMessage(err, "Invalid or expired code")),
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Please enter username and password");
      return;
    }
    loginMutation.mutate({ username: username.trim(), password });
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeSent) {
      if (!email.trim()) return void toast.error("Please enter your email");
      requestCodeMutation.mutate();
    } else {
      if (!/^\d{6}$/.test(code.trim())) return void toast.error("Enter the 6-digit code");
      verifyMutation.mutate();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background via-background to-secondary/30">
      <Card className="w-full max-w-sm border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FolderKanban className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-center">WELLS TRACK</h1>
            <p className="text-sm text-muted-foreground text-center">
              {mode === "password" ? "Sign in with your account" : "Sign in or sign up with email"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 mb-6">
            <button
              type="button"
              className={`rounded-md py-1.5 text-sm font-medium transition-colors ${
                mode === "password" ? "bg-background shadow-sm" : "text-muted-foreground"
              }`}
              onClick={() => setMode("password")}
            >
              Password
            </button>
            <button
              type="button"
              className={`rounded-md py-1.5 text-sm font-medium transition-colors ${
                mode === "email" ? "bg-background shadow-sm" : "text-muted-foreground"
              }`}
              onClick={() => setMode("email")}
            >
              Email code
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  autoFocus
                  autoComplete="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                {loginMutation.isPending ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={codeSent}
                />
              </div>
              {codeSent && (
                <div className="space-y-2">
                  <Label htmlFor="code">Verification code</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    First time here? Verifying the code creates your account automatically.
                  </p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={requestCodeMutation.isPending || verifyMutation.isPending}
              >
                {(requestCodeMutation.isPending || verifyMutation.isPending) && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {codeSent ? "Verify & sign in" : "Send code"}
              </Button>
              {codeSent && (
                <button
                  type="button"
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setCodeSent(false);
                    setCode("");
                  }}
                >
                  Use a different email / resend
                </button>
              )}
            </form>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing you agree to our{" "}
            <a href="/terms" className="underline underline-offset-2 hover:text-foreground">
              Terms
            </a>{" "}
            and{" "}
            <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
              Privacy Policy
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
